import { Loader2, WifiOff } from "lucide-react";
import * as m from "../../paraglide/messages.js";
import {
  useAuthStore,
  useConnectionState,
  type ConnectionState,
} from "../../stores/auth-store.js";

/**
 * Small notice at the top of the screen while the API can't be reached
 * (it restarts on every deploy). The session check keeps the user where they
 * are and retries; this says so. Renders nothing while connected.
 *
 * Mounted once in the root layout, so it shows over the loading screen and
 * over the desktop alike.
 */
export function ConnectionNotice() {
  const connection = useConnectionState();
  const reconnect = useAuthStore((s) => s.reconnect);
  return <ConnectionNoticeView connection={connection} onRetry={reconnect} />;
}

export function ConnectionNoticeView({
  connection,
  onRetry,
}: {
  connection: ConnectionState;
  onRetry: () => void;
}) {
  if (connection === "online") return null;

  return (
    // Above every desktop window: window z-indexes keep climbing from 10.
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[20000] flex justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-[var(--color-border-default)] bg-[var(--color-window-face)] px-3.5 py-1.5 text-sm text-[var(--color-text-body)] shadow-md"
      >
        {connection === "reconnecting" ? (
          <>
            <Loader2
              size={14}
              className="animate-spin text-[var(--color-accent-amber)]"
              aria-hidden
            />
            <span>{m.connection_reconnecting()}</span>
          </>
        ) : (
          <>
            <WifiOff
              size={14}
              className="text-[var(--color-text-warning)]"
              aria-hidden
            />
            <span>{m.connection_unreachable()}</span>
            <button
              type="button"
              onClick={onRetry}
              className="rounded-full px-2 py-0.5 font-medium text-[var(--color-accent-amber-dark)] hover:bg-[var(--color-hover-overlay)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-amber)]"
            >
              {m.connection_try_again()}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
